"use client";

import { useEffect, useMemo, useRef, useState } from "react";

import { CancelSmallIcon, PinAltIcon } from "@/components/icons-lib";
import type {
  DistrictSchoolLevelType,
  IFindMySchoolMapLevelBoundaryItem,
  IFindMySchoolSchoolItem,
  SchoolType,
} from "@/providers/store/api/find-my-school/find-my-school.interface";

import { CategoryTabs } from "./category-tabs";
import { CommunityServicesList } from "./community-services-list";
import {
  FindMySchoolModeTabs,
  type FindMySchoolSidebarMode,
} from "./find-my-school-mode-tabs";
import { SchoolList } from "./school-list";

export type MobileDrawerSnap = "collapsed" | "middle" | "full";

export interface MobileDrawerMetrics {
  height: number;
  controlsOpacity: number;
  mapBottomPadding: number;
}

interface DrawerSnapHeights {
  collapsed: number;
  middle: number;
  full: number;
}

interface FindMySchoolMobileDrawerProps {
  snap: MobileDrawerSnap;
  hasAddress: boolean;
  address: string | null;
  schools: IFindMySchoolSchoolItem[];
  highlightedSchoolId: string | null;
  selectedCategory: SchoolType;
  schoolLevelTypes: readonly DistrictSchoolLevelType[];
  isLoading: boolean;
  outsideDistrictBoundary: boolean;
  hasSearchedAddress: boolean;
  sidebarMode: FindMySchoolSidebarMode;
  showCommunityMode: boolean;
  communityItems: IFindMySchoolMapLevelBoundaryItem[];
  highlightedBoundaryId: string | null;
  isCommunityLoading: boolean;
  onSidebarModeChange: (mode: FindMySchoolSidebarMode) => void;
  onCommunityItemClick: (item: IFindMySchoolMapLevelBoundaryItem) => void;
  onSnapChange: (snap: MobileDrawerSnap) => void;
  onMetricsChange: (metrics: MobileDrawerMetrics) => void;
  onClearAddress: () => void;
  onCategoryChange: (category: SchoolType) => void;
  onSchoolClick: (school: IFindMySchoolSchoolItem) => void;
}

const COLLAPSED_HEIGHT = 150;
const MOBILE_SEARCH_CLEARANCE = 68;
const DRAG_START_THRESHOLD = 6;
const DRAWER_SNAPS: MobileDrawerSnap[] = ["collapsed", "middle", "full"];

function getSnapHeights(containerHeight: number): DrawerSnapHeights {
  const collapsed = Math.min(COLLAPSED_HEIGHT, Math.round(containerHeight * 0.4));
  const middle = Math.max(collapsed, Math.round(containerHeight * 0.55));
  const fullByRatio = Math.round(containerHeight * 0.92);
  const fullBelowSearch = containerHeight - MOBILE_SEARCH_CLEARANCE;
  const full = Math.max(middle + 48, Math.min(fullByRatio, fullBelowSearch));

  return { collapsed, middle, full };
}

function getDrawerHint(
  snap: MobileDrawerSnap,
  hasAddress: boolean,
  dragging: boolean,
): string {
  if (dragging) return "Release to snap";
  if (snap === "full") return "Drag down for the map";
  if (snap === "middle") return "Drag for the full list";
  if (hasAddress) return "Drag up for results";
  return "Drag to see school info";
}

function getControlsOpacity(height: number, snapHeights: DrawerSnapHeights): number {
  const fadeStart = snapHeights.middle + (snapHeights.full - snapHeights.middle) * 0.55;
  if (height <= fadeStart) return 1;
  if (height >= snapHeights.full) return 0;
  return 1 - (height - fadeStart) / (snapHeights.full - fadeStart);
}

function findNearestSnap(height: number, snapHeights: DrawerSnapHeights): MobileDrawerSnap {
  let nearestSnap: MobileDrawerSnap = "collapsed";
  let nearestDistance = Math.abs(height - snapHeights.collapsed);

  for (const candidateSnap of DRAWER_SNAPS) {
    const candidateDistance = Math.abs(height - snapHeights[candidateSnap]);
    if (candidateDistance < nearestDistance) {
      nearestDistance = candidateDistance;
      nearestSnap = candidateSnap;
    }
  }

  return nearestSnap;
}

function getKeyboardSnap(
  currentSnap: MobileDrawerSnap,
  direction: "up" | "down",
): MobileDrawerSnap {
  const currentIndex = DRAWER_SNAPS.indexOf(currentSnap);
  const indexDelta = direction === "up" ? 1 : -1;
  const nextIndex = Math.min(DRAWER_SNAPS.length - 1, Math.max(0, currentIndex + indexDelta));
  return DRAWER_SNAPS[nextIndex] ?? currentSnap;
}

function getSheetTitle(
  mode: FindMySchoolSidebarMode,
  hasAddress: boolean,
  schoolCount: number,
  isLoading: boolean,
): string {
  if (mode === "community") {
    return hasAddress
      ? "Community & services for this address"
      : "Community & services";
  }
  if (!hasAddress) return "Schools in the district";
  if (isLoading) return "Your assigned schools";
  if (schoolCount === 1) return "Your 1 assigned school";
  return `Your ${schoolCount} assigned schools`;
}

function getSchoolCountLabel(schoolCount: number): string {
  const noun = schoolCount === 1 ? "school" : "schools";
  return `${schoolCount} ${noun}`;
}

function findActiveTouch(touches: React.TouchList, touchId: number | null): React.Touch | null {
  if (touchId == null) return null;
  for (const touch of Array.from(touches)) {
    if (touch.identifier === touchId) return touch;
  }
  return null;
}

export function FindMySchoolMobileDrawer({
  snap,
  hasAddress,
  address,
  schools,
  highlightedSchoolId,
  selectedCategory,
  schoolLevelTypes,
  isLoading,
  outsideDistrictBoundary,
  hasSearchedAddress,
  sidebarMode,
  showCommunityMode,
  communityItems,
  highlightedBoundaryId,
  isCommunityLoading,
  onSidebarModeChange,
  onCommunityItemClick,
  onSnapChange,
  onMetricsChange,
  onClearAddress,
  onCategoryChange,
  onSchoolClick,
}: FindMySchoolMobileDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const dragStartYRef = useRef(0);
  const dragStartHeightRef = useRef(0);
  const currentDragHeightRef = useRef<number | null>(null);
  const dragSnapHeightsRef = useRef<DrawerSnapHeights | null>(null);
  const activeTouchIdRef = useRef<number | null>(null);
  const dragMovedRef = useRef(false);
  const [containerHeight, setContainerHeight] = useState(720);
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const [dragMoved, setDragMoved] = useState(false);

  useEffect(() => {
    const container = drawerRef.current?.parentElement;
    if (!container) return;

    const updateContainerHeight = () => {
      setContainerHeight(container.clientHeight);
    };

    updateContainerHeight();
    if (typeof ResizeObserver === "undefined") return;

    const resizeObserver = new ResizeObserver(updateContainerHeight);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return;

    const preventPageScroll = (event: TouchEvent) => {
      if (activeTouchIdRef.current != null) event.preventDefault();
    };

    handle.addEventListener("touchmove", preventPageScroll, { passive: false });
    return () => handle.removeEventListener("touchmove", preventPageScroll);
  }, []);

  const snapHeights = useMemo(() => getSnapHeights(containerHeight), [containerHeight]);
  const sheetHeight = dragHeight ?? snapHeights[snap];
  const dragging = dragHeight != null;
  const activeSnapHeights = dragHeight == null
    ? snapHeights
    : (dragSnapHeightsRef.current ?? snapHeights);
  const controlsOpacity = getControlsOpacity(sheetHeight, activeSnapHeights);
  const mapBottomPadding = snap === "collapsed"
    ? activeSnapHeights.collapsed
    : activeSnapHeights.middle;

  useEffect(() => {
    onMetricsChange({
      height: sheetHeight,
      controlsOpacity,
      mapBottomPadding,
    });
  }, [controlsOpacity, mapBottomPadding, onMetricsChange, sheetHeight]);

  const startDrag = (clientY: number) => {
    const renderedHeight = drawerRef.current?.getBoundingClientRect().height ?? sheetHeight;
    dragStartYRef.current = clientY;
    dragStartHeightRef.current = renderedHeight;
    currentDragHeightRef.current = renderedHeight;
    dragSnapHeightsRef.current = snapHeights;
    dragMovedRef.current = false;
    setDragMoved(false);
    setDragHeight(renderedHeight);
  };

  const updateDrag = (clientY: number) => {
    if (currentDragHeightRef.current == null) return;
    const dragDelta = dragStartYRef.current - clientY;
    if (!dragMovedRef.current) {
      if (Math.abs(dragDelta) < DRAG_START_THRESHOLD) return;
      dragMovedRef.current = true;
      setDragMoved(true);
    }
    const dragLimits = dragSnapHeightsRef.current ?? snapHeights;
    const nextHeight = Math.min(
      dragLimits.full,
      Math.max(dragLimits.collapsed, dragStartHeightRef.current + dragDelta),
    );
    currentDragHeightRef.current = nextHeight;
    setDragHeight(nextHeight);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType !== "mouse" && event.pointerType !== "pen") return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    startDrag(event.clientY);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType !== "mouse" && event.pointerType !== "pen") return;
    updateDrag(event.clientY);
  };

  const finishDrag = (cancelled = false) => {
    const finalHeight = currentDragHeightRef.current;
    if (finalHeight == null) return;
    const dragLimits = dragSnapHeightsRef.current ?? snapHeights;
    let nextSnap = snap;
    if (!cancelled && dragMovedRef.current) {
      nextSnap = findNearestSnap(finalHeight, dragLimits);
    }
    currentDragHeightRef.current = null;
    dragSnapHeightsRef.current = null;
    dragMovedRef.current = false;
    setDragHeight(null);
    setDragMoved(false);
    if (nextSnap !== snap) onSnapChange(nextSnap);
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType !== "mouse" && event.pointerType !== "pen") return;
    finishDrag(event.type === "pointercancel");
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLButtonElement>) => {
    if (event.touches.length !== 1 || activeTouchIdRef.current != null) return;
    const touch = event.touches.item(0);
    if (!touch) return;
    activeTouchIdRef.current = touch.identifier;
    startDrag(touch.clientY);
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLButtonElement>) => {
    const touch = findActiveTouch(event.touches, activeTouchIdRef.current);
    if (!touch) return;
    updateDrag(touch.clientY);
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLButtonElement>) => {
    if (!findActiveTouch(event.changedTouches, activeTouchIdRef.current)) return;
    activeTouchIdRef.current = null;
    finishDrag(event.type === "touchcancel");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowUp") {
      event.preventDefault();
      onSnapChange(getKeyboardSnap(snap, "up"));
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      onSnapChange(getKeyboardSnap(snap, "down"));
    }
  };

  const sheetTitle = getSheetTitle(sidebarMode, hasAddress, schools.length, isLoading);
  const sheetHint = getDrawerHint(snap, hasAddress, dragging && dragMoved);
  const showSchoolCount = sidebarMode === "schools" && !isLoading;

  return (
    <div
      ref={drawerRef}
      className="absolute inset-x-0 bottom-0 z-30 flex max-h-full flex-col rounded-t-[18px] bg-white shadow-[0_-6px_22px_rgba(0,0,0,0.18)]"
      style={{
        height: sheetHeight,
        maxHeight: activeSnapHeights.full,
        transition: dragging ? "none" : "height 220ms cubic-bezier(.2,.8,.2,1)",
      }}
    >
      <button
        ref={handleRef}
        aria-label={`School drawer: ${snap}`}
        className="flex shrink-0 cursor-grab touch-none flex-col items-center gap-1 bg-transparent px-0 pb-2 pt-2.5 active:cursor-grabbing"
        type="button"
        onKeyDown={handleKeyDown}
        onPointerCancel={handlePointerEnd}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onTouchCancel={handleTouchEnd}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onTouchStart={handleTouchStart}
      >
        <span className="h-[5px] w-11 rounded-full bg-[#CFCBCC]" />
        <span className="text-[11px]/[15px] font-semibold tracking-[0.04em] text-[#8A8586]">
          {sheetHint}
        </span>
      </button>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain px-3 pb-[calc(20px+env(safe-area-inset-bottom))]">
        {hasAddress && address && (
          <div className="flex shrink-0 items-start gap-2 rounded-[10px] bg-[#F0EFEE] px-3 py-2.5 text-[13px]/[18px] text-[#161515]">
            <PinAltIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#A11733]" />
            <span className="min-w-0 flex-1 break-words font-medium">{address}</span>
            <button
              aria-label="Clear address"
              className="-mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md hover:bg-black/5"
              type="button"
              onClick={onClearAddress}
            >
              <CancelSmallIcon className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="flex shrink-0 items-baseline justify-between gap-2.5">
          <h2 className="m-0 text-[17px]/[23px] font-bold text-[#161515]">{sheetTitle}</h2>
          {showSchoolCount && (
            <span className="shrink-0 text-[13px]/[18px] text-[#6B6667]">
              {getSchoolCountLabel(schools.length)}
            </span>
          )}
        </div>

        {showCommunityMode && (
          <div className="shrink-0">
            <FindMySchoolModeTabs
              mobile
              selectedMode={sidebarMode}
              onChange={onSidebarModeChange}
            />
          </div>
        )}

        {sidebarMode === "schools" ? (
          <>
            <div className="shrink-0">
              <CategoryTabs
                mobile
                schoolLevelTypes={schoolLevelTypes}
                selectedCategory={selectedCategory}
                onChange={onCategoryChange}
              />
            </div>

            <div className="shrink-0 pb-1">
              <SchoolList
                hasSearchedAddress={hasSearchedAddress}
                highlightedSchoolId={highlightedSchoolId}
                isLoading={isLoading}
                outsideDistrictBoundary={outsideDistrictBoundary}
                schools={schools}
                variant="mobile"
                onSchoolClick={onSchoolClick}
              />
            </div>
          </>
        ) : (
          <div className="shrink-0 pb-1">
            <CommunityServicesList
              hasSearchedAddress={hasAddress}
              highlightedBoundaryId={highlightedBoundaryId}
              isLoading={isCommunityLoading}
              items={communityItems}
              variant="mobile"
              onItemClick={onCommunityItemClick}
            />
          </div>
        )}
      </div>
    </div>
  );
}
